import { PlusIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useState } from "react";
import type { Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { type ComparisonDimension, ComparisonDimensions } from "#shared/comparisonDimension.ts";
import type { ComparisonBreakdown } from "#shared/comparisonSelection.ts";
import { Button } from "@/components/ui/button.tsx";
import { ListBox, ListBoxItem, ListBoxLabel } from "@/components/ui/list-box.tsx";
import { Note } from "@/components/ui/note.tsx";
import { Popover, PopoverContent } from "@/components/ui/popover.tsx";
import { Text } from "@/components/ui/text.tsx";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { CapTooltip } from "@packages/core-client/components/CapTooltip.tsx";
import { ComparisonDimensionPanel } from "./ComparisonDimensionPanel.tsx";

type OpenView = ComparisonDimension | null;

const NO_KEYS: string[] = [];

export interface BreakdownButtonProps {
    nodeId: string;
    dimension?: ComparisonDimension;
    breakdown: ComparisonBreakdown | undefined;
    maxSelections: number;
    usedSeriesCount: number;
    maxSeriesCount: number;
    size?: "md" | "sm";
    onBreakdownChange: (nodeId: string, dimension: ComparisonDimension, groupKeys: string[]) => void;
}

export function BreakdownButton({ nodeId, dimension, breakdown, maxSelections, usedSeriesCount, maxSeriesCount, size = "md", onBreakdownChange }: BreakdownButtonProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("BreakdownButton", { i18n: i18nextInstance });
    const { t: tDimensions } = useTranslation("CalibrationDimensions", { i18n: i18nextInstance });

    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<OpenView>(null);

    const isFull = usedSeriesCount >= maxSeriesCount;

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (nextOpen && isFull) {
                return;
            }

            setIsOpen(nextOpen);

            if (nextOpen) {
                setView(dimension ?? breakdown?.dimension ?? null);
            }
        },
        [dimension, breakdown, isFull]
    );

    const handleClose = useCallback(() => setIsOpen(false), []);

    const handleBack = useCallback(() => (dimension ? setIsOpen(false) : setView(null)), [dimension]);

    const handleChoose = useCallback((key: Key) => setView(ComparisonDimensions.find(candidate => candidate === key) ?? null), []);

    const handleGroupsChange = useCallback(
        (groupKeys: string[]) => {
            if (view) {
                onBreakdownChange(nodeId, view, groupKeys);
            }
        },
        [view, nodeId, onBreakdownChange]
    );

    const selectedKeys = view && view === breakdown?.dimension ? breakdown.groupKeys : NO_KEYS;

    return (
        <Popover isOpen={isOpen} onOpenChange={handleOpenChange}>
            <CapTooltip isShown={isFull} label={t("capReached", { count: maxSeriesCount })}>
                <Button intent="plain" size="sm" aria-disabled={isFull} className={`text-muted-fg hover:text-fg ${size === "sm" ? "h-7 px-2 text-xs" : "h-9"} ${isFull ? "opacity-50" : ""}`}>
                    <PlusIcon aria-hidden="true" />
                    {dimension ? t("addGroup", { dimension: tDimensions(dimension) }) : t("label")}
                </Button>
            </CapTooltip>
            <PopoverContent placement="bottom start" aria-label={t("label")} className="bg-bg [&_[data-slot=popover-inner]]:overflow-x-hidden [&_[data-slot=popover-inner]]:p-3">
                {view ? (
                    <ComparisonDimensionPanel dimension={view} selectedKeys={selectedKeys} onSelectionChange={handleGroupsChange} onBack={handleBack} maxSelections={maxSelections} />
                ) : (
                    <ListBox aria-label={t("label")} className="max-h-none min-h-0 w-64 border-0" onAction={handleChoose}>
                        {ComparisonDimensions.map(candidate => (
                            <ListBoxItem key={candidate} id={candidate} textValue={tDimensions(candidate)}>
                                <ListBoxLabel>{tDimensions(candidate)}</ListBoxLabel>
                            </ListBoxItem>
                        ))}
                    </ListBox>
                )}
                {view && (
                    <div className="mt-3 shrink-0 space-y-3">
                        <output className="sr-only">{t("liveRegion.used", { count: usedSeriesCount, total: maxSeriesCount })}</output>
                        {isFull ? (
                            <Note intent="warning" role="alert">
                                {t("capReached", { count: maxSeriesCount })}
                            </Note>
                        ) : (
                            <Text className="text-muted-fg text-xs">{t("hint", { count: usedSeriesCount, total: maxSeriesCount })}</Text>
                        )}
                        <div className="flex justify-end">
                            <Button size="sm" onPress={handleClose}>
                                {t("done")}
                            </Button>
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
